import React from 'react';
import { GitCompareArrows } from 'lucide-react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import CountUp from '../CountUp';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 31 — Matching Bonus.
 * 5 identical 5% levels (Level 1 – Level 5). Presented as a horizontal row of
 * cards with a mirror/match icon (matches the "matching" concept), followed
 * by the same big total treatment we use on Unilevel (Bis zu 25%).
 */
export default function SectionMatchingBonus() {
  const { t } = useT();
  const m = t.matching;

  return (
    <SectionShell id="matching-bonus" className="">
      <div className="grid grid-cols-12 gap-y-10 md:gap-12 mb-14 items-end">
        <div className="col-span-12 md:col-span-4">
          <Reveal>
            <div className="overline mb-6">{m.overline}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white max-w-[16ch]">
              {m.title}
            </h2>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-8">
          <Reveal delay={120}>
            {(() => {
              // Split sub into 3 lines: sentence 1, then split sentence 2 at
              // the em-dash so the "und das bis in die 5. Ebene…" clause
              // sits on its own line.
              const parts = m.sub.split(/\.\s+/).filter(Boolean);
              const first = parts[0] ? parts[0] + '.' : m.sub;
              const rest = parts.slice(1).join('. ');
              // Break rest at em-dash / hyphen for natural 3rd line
              let second = rest;
              let third = '';
              const dashIdx = rest.search(/\s[–—-]\s/);
              if (dashIdx > 0) {
                second = rest.slice(0, dashIdx).trim();
                third = rest.slice(dashIdx).trim();
              }
              const lineCls = 'font-ui text-3xl md:text-4xl lg:text-[2.6rem] text-white/85 leading-snug';
              return (
                <>
                  <p className={lineCls}>{first}</p>
                  {second && <p className={`${lineCls} mt-3`}>{second}</p>}
                  {third && <p className={`${lineCls} mt-3`}>{third}</p>}
                </>
              );
            })()}
          </Reveal>
        </div>
      </div>

      {/* 5 level cards in a horizontal row */}
      <div className="matching-row" data-testid="matching-row">
        {m.levels.map((lvl, i) => (
          <Reveal key={i} delay={140 + i * 90}>
            <div className="matching-card group" data-testid={`matching-${i + 1}`} style={{ '--step': i }}>
              <div className="matching-card-inner">
                <div className="matching-icon">
                  <GitCompareArrows
                    strokeWidth={1.3}
                    className="w-6 h-6 md:w-7 md:h-7 text-[#f2d979]"
                    aria-hidden
                  />
                </div>
                <div className="matching-level font-display">{lvl.level}</div>
                <div className="matching-value font-display">{lvl.value}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Info note */}
      <Reveal delay={640}>
        <p
          className="font-ui text-2xl md:text-3xl text-white/80 italic text-center mt-8 max-w-[46em] mx-auto"
          style={{ whiteSpace: 'pre-line' }}
          data-testid="matching-note"
        >
          {m.note}
        </p>
      </Reveal>

      {/* Big total */}
      <Reveal delay={760}>
        <div className="unilevel-total mt-12" data-testid="matching-total">
          <span className="unilevel-total-line" aria-hidden />
          <div className="unilevel-total-block">
            <span className="unilevel-total-big font-display">{m.totalPrefix} </span>
            <span className="unilevel-total-huge font-display"><CountUp value={m.totalValue} /></span>
            <span className="unilevel-total-big font-display"> {m.totalSuffix}</span>
          </div>
          <span className="unilevel-total-line" aria-hidden />
        </div>
      </Reveal>
    </SectionShell>
  );
}
